'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { AdvancedFilter } from '@morpho-org/ui-v6/client';
import { capitalize, isEqual } from 'lodash-es';
import { memo, type ReactNode, useCallback, useMemo, useState } from 'react';
import { TagAssetFilter } from '@/components/common/TableAdvancedFilters/AssetAdvancedFilter/TagAssetFilter';
import { AssetFilter as AssetFilterType } from '@/helpers/filter/table';
export interface AssetItem {
  id: string;
  address: Address;
  symbol: string;
  tags: string[] | null;
  logoURI: string | null;
  chain: {
    id: number;
    network?: string;
  };
  /** Total matching vault count from the API facet, shown next to the option. */
  count?: number;
}
interface AssetFilterProps {
  label: string;
  allLabel?: string;
  allAssets: AssetItem[];
  assets: string[];
  chainIds?: ChainId[];
  onAssetsChange: (assets: string[]) => void;
  disabled: boolean;
  hasHighestZIndex?: boolean;
  onClear?: () => void;
  showPTFilter?: boolean;
  getAssetSelector?: (asset: AssetItem) => string;
  resultLabel?: ReactNode;
}
interface FilterDefinition {
  value: AssetFilterType;
  label: string;
  tooltip?: string;
  filterMethod: 'tag' | 'prefix';
  filterValue?: string;
}
const BASE_FILTER_DEFINITIONS: FilterDefinition[] = [{
  value: AssetFilterType.ALL,
  label: 'All',
  filterMethod: 'tag'
}, {
  value: AssetFilterType.ETH,
  label: 'Eth',
  tooltip: 'All ETH related/derivative assets.',
  filterMethod: 'tag'
}, {
  value: AssetFilterType.BTC,
  label: 'Btc',
  tooltip: 'All BTC related/derivative assets.',
  filterMethod: 'tag'
}, {
  value: AssetFilterType.STABLECOIN,
  label: 'Stables',
  tooltip: 'Assets pegged to fiat currencies.',
  filterMethod: 'tag'
}];
const defaultGetAssetSelector = (asset: AssetItem): string => asset.id;
const hasAssetSelector = (selectors: string[], selector: string) => selectors.some(value => value.toLowerCase() === selector.toLowerCase());
const removeAssetSelector = (selectors: string[], selector: string) => selectors.filter(value => value.toLowerCase() !== selector.toLowerCase());
const toggleAssetSelector = (selectors: string[], selector: string) => hasAssetSelector(selectors, selector) ? removeAssetSelector(selectors, selector) : [...selectors, selector];
const areAssetSelectorSetsEqual = (left: string[], right: string[]) => isEqual(new Set(left.map(value => value.toLowerCase())), new Set(right.map(value => value.toLowerCase())));
const AssetFilter = t0 => {
  const $ = _c(71);
  const {
    label,
    allLabel,
    allAssets,
    assets,
    chainIds,
    disabled,
    hasHighestZIndex,
    onAssetsChange,
    onClear,
    showPTFilter: t1,
    getAssetSelector: t2,
    resultLabel
  } = t0;
  const showPTFilter = t1 === undefined ? false : t1;
  const getAssetSelector = t2 === undefined ? defaultGetAssetSelector : t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [sortedIds, setSortedIds] = useState(t3);
  let t4;
  if ($[1] !== allAssets || $[2] !== assets || $[3] !== chainIds || $[4] !== getAssetSelector || $[5] !== sortedIds) {
    bb0: {
      let t5;
      if ($[7] !== chainIds) {
        t5 = asset => {
          if (!chainIds?.length) {
            return true;
          }
          return chainIds.includes(asset.chain.id);
        };
        $[7] = chainIds;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      let t6;
      if ($[9] !== assets || $[10] !== chainIds || $[11] !== getAssetSelector) {
        t6 = asset_0 => {
          const value = getAssetSelector(asset_0);
          return {
            id: asset_0.id,
            label: asset_0.symbol,
            value,
            isChecked: hasAssetSelector(assets, value),
            img: asset_0.logoURI,
            chain: chainIds && {
              id: asset_0.chain.id,
              name: capitalize(asset_0.chain.network)
            },
            count: asset_0.count
          };
        };
        $[9] = assets;
        $[10] = chainIds;
        $[11] = getAssetSelector;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      const mapped = allAssets.filter(t5).map(t6);
      if (sortedIds.length === 0) {
        t4 = mapped;
        break bb0;
      }
      let t7;
      if ($[13] !== sortedIds) {
        t7 = new Map(sortedIds.map(_temp));
        $[13] = sortedIds;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      const orderIndex = t7;
      let t8;
      if ($[15] !== orderIndex) {
        t8 = (a, b) => {
          const aIdx = orderIndex.get(a.id) ?? Infinity;
          const bIdx = orderIndex.get(b.id) ?? Infinity;
          return aIdx - bIdx;
        };
        $[15] = orderIndex;
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      t4 = mapped.toSorted(t8);
    }
    $[1] = allAssets;
    $[2] = assets;
    $[3] = chainIds;
    $[4] = getAssetSelector;
    $[5] = sortedIds;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const items = t4;
  let t5;
  if ($[17] !== allAssets || $[18] !== assets || $[19] !== getAssetSelector) {
    t5 = newAssets => {
      const filter = newAssets ?? assets;
      const sorted = [...allAssets].toSorted((a_0, b_0) => {
        const aInFilter = Number(hasAssetSelector(filter, getAssetSelector(a_0)));
        const bInFilter = Number(hasAssetSelector(filter, getAssetSelector(b_0)));
        return bInFilter - aInFilter || a_0.symbol.localeCompare(b_0.symbol);
      });
      setSortedIds(sorted.map(_temp2));
    };
    $[17] = allAssets;
    $[18] = assets;
    $[19] = getAssetSelector;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const sortAssets = t5;
  let t6;
  if ($[21] !== assets || $[22] !== onAssetsChange) {
    t6 = value_0 => {
      const newAssets_0 = toggleAssetSelector(assets, value_0);
      onAssetsChange(newAssets_0);
    };
    $[21] = assets;
    $[22] = onAssetsChange;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const toggleItem = t6;
  let t7;
  if ($[24] !== allAssets || $[25] !== chainIds || $[26] !== getAssetSelector) {
    t7 = filterDef => {
      if (filterDef.value === AssetFilterType.ALL) {
        return [];
      }
      const chainFilteredAssets = chainIds?.length ? allAssets.filter(asset_2 => chainIds.includes(asset_2.chain.id)) : allAssets;
      const filterFn = filterDef.filterMethod === "tag" ? asset_3 => asset_3.tags?.includes(filterDef.value) : asset_4 => asset_4.symbol.startsWith(filterDef.filterValue || filterDef.value);
      return chainFilteredAssets.filter(filterFn).map(getAssetSelector);
    };
    $[24] = allAssets;
    $[25] = chainIds;
    $[26] = getAssetSelector;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const getAssetsByFilter = t7;
  let t8;
  if ($[28] !== getAssetsByFilter || $[29] !== onAssetsChange || $[30] !== sortAssets) {
    t8 = filterDef_0 => {
      const newAssets_1 = getAssetsByFilter(filterDef_0);
      onAssetsChange(newAssets_1);
      sortAssets(newAssets_1);
    };
    $[28] = getAssetsByFilter;
    $[29] = onAssetsChange;
    $[30] = sortAssets;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const handleFilterChange = t8;
  let t9;
  if ($[32] !== onAssetsChange || $[33] !== onClear || $[34] !== sortAssets) {
    t9 = () => {
      onAssetsChange([]);
      sortAssets([]);
      onClear?.();
    };
    $[32] = onAssetsChange;
    $[33] = onClear;
    $[34] = sortAssets;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const clear = t9;
  let t10;
  if ($[36] !== allAssets) {
    t10 = allAssets.some(_temp3);
    $[36] = allAssets;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const hasPTAssets = t10;
  let t11;
  if ($[38] !== allAssets) {
    t11 = allAssets.some(_temp4);
    $[38] = allAssets;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const hasStockAssets = t11;
  let t12;
  if ($[40] !== hasPTAssets || $[41] !== showPTFilter) {
    t12 = showPTFilter && hasPTAssets ? [{
      value: AssetFilterType.PT,
      label: "PT",
      tooltip: "All Principal Token (PT) assets.",
      filterMethod: "prefix",
      filterValue: "PT-"
    } satisfies FilterDefinition] : [];
    $[40] = hasPTAssets;
    $[41] = showPTFilter;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== hasStockAssets) {
    t13 = hasStockAssets ? [{
      value: AssetFilterType.STOCKS,
      label: "Stocks",
      tooltip: "Tokenized stock assets.",
      filterMethod: "tag"
    } satisfies FilterDefinition] : [];
    $[43] = hasStockAssets;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t12 || $[46] !== t13) {
    t14 = [...BASE_FILTER_DEFINITIONS, ...t12, ...t13];
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const filterDefinitions = t14;
  let t15;
  if ($[48] !== assets || $[49] !== filterDefinitions || $[50] !== getAssetsByFilter || $[51] !== handleFilterChange) {
    t15 = filterDefinitions.map(filterDef_1 => {
      const filteredAssets = getAssetsByFilter(filterDef_1);
      const isActive = !assets.length && filterDef_1.value === AssetFilterType.ALL ? true : filteredAssets.length > 0 && areAssetSelectorSetsEqual(filteredAssets, assets);
      return {
        value: filterDef_1.value,
        label: filterDef_1.label,
        isActive,
        isDisabled: filterDef_1.value !== AssetFilterType.ALL && filteredAssets.length === 0,
        action: () => handleFilterChange(filterDef_1),
        tooltip: filterDef_1.tooltip
      };
    });
    $[48] = assets;
    $[49] = filterDefinitions;
    $[50] = getAssetsByFilter;
    $[51] = handleFilterChange;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const tagOptions = t15;
  let t16;
  if ($[53] !== disabled || $[54] !== tagOptions) {
    t16 = <TagAssetFilter tagOptions={tagOptions} disabled={disabled} />;
    $[53] = disabled;
    $[54] = tagOptions;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const t17 = label === "asset" ? "Search for asset" : `Search for ${label} asset`;
  let t18;
  if ($[56] !== label) {
    t18 = capitalize(label);
    $[56] = label;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  const t19 = `Remove ${t18} filter`;
  const t20 = assets.length > 0;
  let t21;
  if ($[58] !== allLabel || $[59] !== clear || $[60] !== disabled || $[61] !== hasHighestZIndex || $[62] !== items || $[63] !== resultLabel || $[64] !== sortAssets || $[65] !== t16 || $[66] !== t17 || $[67] !== t19 || $[68] !== t20 || $[69] !== toggleItem) {
    t21 = <AdvancedFilter items={items} sortItems={sortAssets} toggleItem={toggleItem} clear={clear} slot={t16} inputPlaceholder={t17} clearTooltipText={t19} disabled={disabled} hasHighestZIndex={hasHighestZIndex} allLabel={allLabel} resultLabel={resultLabel} hasSelection={t20} />;
    $[58] = allLabel;
    $[59] = clear;
    $[60] = disabled;
    $[61] = hasHighestZIndex;
    $[62] = items;
    $[63] = resultLabel;
    $[64] = sortAssets;
    $[65] = t16;
    $[66] = t17;
    $[67] = t19;
    $[68] = t20;
    $[69] = toggleItem;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  return t21;
};
export default memo(AssetFilter);
function _temp(id, index) {
  return [id, index];
}
function _temp2(asset_1) {
  return asset_1.id;
}
function _temp3(asset_5) {
  return asset_5.symbol.startsWith("PT-");
}
function _temp4(asset_6) {
  return asset_6.tags?.includes(AssetFilterType.STOCKS);
}